import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAlert } from '../context/alert/CustomAlertProvider';
import { prepareData } from '../components/utils/generateScheduleUtils';
import { useTranslation } from 'react-i18next';

export const useFormNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirmation, showExitConfirmation } = useAlert();
  const { t } = useTranslation();


  const navigationState = useRef({
    isNavigatingAway: false,
    pendingNavigation: null,
    hasUnsavedChanges: true
  });

  const logDebugInfo = (event, extra = {}) => {
    console.log(`[Navigation Debug] Event: ${event}`);
    console.log(`[Navigation Debug] Path: ${location.pathname}`);
    console.log(`[Navigation Debug] isNavigatingAway: ${navigationState.current.isNavigatingAway}`);
    console.log(`[Navigation Debug] hasUnsavedChanges: ${navigationState.current.hasUnsavedChanges}`);
    console.log(`[Navigation Debug] Error state: ${sessionStorage.getItem('generationError')}`);
    console.log(`[Navigation Debug] Is form path: ${isFormPath()}`);
    if (Object.keys(extra).length > 0) {
      console.log('[Navigation Debug] Extra:', extra);
    }
  };

  const isFormPath = () => {
    return location.pathname.includes('/form/') ||
      location.pathname.includes('/result');
  };

  const isCloneOrDraft = () => {
    const cloneInfo = sessionStorage.getItem('cloneInfo');
    const draftInfo = sessionStorage.getItem('draftInfo');
    return !!(cloneInfo || draftInfo);
  };

  useEffect(() => {
    if (isFormPath()) {
      navigationState.current.hasUnsavedChanges = true;
    }
  }, [location.pathname]);

  useEffect(() => {
    const shouldConfirmNavigation = () => {
      if (location.state?.isExample) return false;

      return (isFormPath() && navigationState.current.hasUnsavedChanges) ||
        (isFormPath() && isCloneOrDraft());
    };

    const hasInfeasibleError = () => {
      return sessionStorage.getItem('generationError') === 'NO_SOLUTION';
    };

    const saveCurrentDraftAndNavigate = async (targetPath) => {
      logDebugInfo('saveCurrentDraftAndNavigate', { targetPath });
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Token not found');
        }

        const data = prepareData();

        const scenarioId = JSON.parse(sessionStorage.getItem('currentScenario'))?.id ||
          JSON.parse(sessionStorage.getItem('scenarioId'))?.scenarioId || 1;


        const response = await fetch(`https://www.ia-schedule.com:4139/api/roster/drafts?scenarioId=${scenarioId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error('Failed to save draft');
        }


        sessionStorage.clear();
        navigationState.current.hasUnsavedChanges = false;
        navigationState.current.isNavigatingAway = false;
        navigate(targetPath);
      } catch (error) {
        console.error('[Navigation Debug] Error saving draft:', error);
        sessionStorage.clear();
        navigationState.current.hasUnsavedChanges = false;
        navigationState.current.isNavigatingAway = false;
        navigate(targetPath);
      }
    };

    const handlePopState = (e) => {
      logDebugInfo('popstate');

      const shouldConfirm = shouldConfirmNavigation();
      const infeasibleError = hasInfeasibleError();

      if ((shouldConfirm || infeasibleError) &&
        !navigationState.current.isNavigatingAway) {
        e.preventDefault();

        const currentPath = window.location.pathname;

        navigationState.current.isNavigatingAway = true;

        if (infeasibleError) {

          showExitConfirmation({
            title: t('navigation.exitConfirm.title'),
            message: t('navigation.exitConfirm.message'),
            primaryButtonText: t('navigation.exitConfirm.saveAndExit'),
            secondaryButtonText: t('navigation.exitConfirm.cancel'),
            tertiaryButtonText: t('navigation.exitConfirm.exitWithoutSaving'),
            onSaveAndExit: () => {
              saveCurrentDraftAndNavigate('/');
            },
            onExitWithoutSaving: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              window.history.back();
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              window.history.pushState(null, '', currentPath);
            }
          });
        } else {

          showConfirmation({
            title: t('navigation.confirm.title'),
            message: t('navigation.confirm.message'),
            confirmText: t('navigation.confirm.confirmText'),
            cancelText: t('navigation.confirm.cancelText'),
            onConfirm: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              window.history.back();
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              window.history.pushState(null, '', currentPath);
            }
          });
        }

        return false;
      }
    };

    const handleLogoClick = (e) => {
      const logoElement = e.target.closest('a[href="/"]');
      if (!logoElement) return;

      logDebugInfo('logoClick', { isLogoElement: !!logoElement });

      const shouldConfirm = shouldConfirmNavigation();
      const infeasibleError = hasInfeasibleError();

      if ((shouldConfirm || infeasibleError) &&
        !navigationState.current.isNavigatingAway) {
        e.preventDefault();
        e.stopPropagation();

        navigationState.current.isNavigatingAway = true;
        navigationState.current.pendingNavigation = '/';

        if (infeasibleError) {

          showExitConfirmation({
            title: t('navigation.exitConfirm.title'),
            message: t('navigation.exitConfirm.message'),
            primaryButtonText: t('navigation.exitConfirm.saveAndExit'),
            secondaryButtonText: t('navigation.exitConfirm.cancel'),
            tertiaryButtonText: t('navigation.exitConfirm.exitWithoutSaving'),
            onSaveAndExit: () => {
              saveCurrentDraftAndNavigate('/');
            },
            onExitWithoutSaving: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate('/');
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        } else {

          showConfirmation({
            title: t('navigation.confirm.title'),
            message: t('navigation.confirm.message'),
            confirmText: t('navigation.confirm.confirmText'),
            cancelText: t('navigation.confirm.cancelText'),
            onConfirm: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate('/');
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        }

        return false;
      } else if (logoElement && location.pathname.includes('/form/') && location.state?.isExample) {
        e.preventDefault();
        sessionStorage.clear();
        navigate('/');
      }
    };

    const handleHeaderClick = (e) => {
      const headerLink = e.target.closest('nav a');
      if (!headerLink) return;

      logDebugInfo('headerClick', {
        href: headerLink.getAttribute('href'),
        isLink: true
      });

      const shouldConfirm = shouldConfirmNavigation();
      const infeasibleError = hasInfeasibleError();

      if ((shouldConfirm || infeasibleError) &&
        !navigationState.current.isNavigatingAway) {
        e.preventDefault();
        e.stopPropagation();

        const targetHref = headerLink.getAttribute('href');

        navigationState.current.isNavigatingAway = true;
        navigationState.current.pendingNavigation = targetHref;

        if (infeasibleError) {

          showExitConfirmation({
            title: t('navigation.exitConfirm.title'),
            message: t('navigation.exitConfirm.message'),
            primaryButtonText: t('navigation.exitConfirm.saveAndExit'),
            secondaryButtonText: t('navigation.exitConfirm.cancel'),
            tertiaryButtonText: t('navigation.exitConfirm.exitWithoutSaving'),
            onSaveAndExit: () => {
              saveCurrentDraftAndNavigate(targetHref);
            },
            onExitWithoutSaving: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate(targetHref);
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        } else {

          showConfirmation({
            title: t('navigation.confirm.title'),
            message: t('navigation.confirm.message'),
            confirmText: t('navigation.confirm.confirmText'),
            cancelText: t('navigation.confirm.cancelText'),
            onConfirm: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate(targetHref);
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        }

        return false;
      }
    };

    const handleLinkClick = (e) => {
      const navLink = e.target.closest('a:not([href="#"])');
      if (!navLink) return;

      const isBackButton = e.target.closest('[data-back-button="true"]');
      if (isBackButton) return;

      const isHeaderLink = e.target.closest('header nav a');
      if (isHeaderLink) return;

      logDebugInfo('linkClick', {
        href: navLink.getAttribute('href'),
        isHeaderLink,
        isBackButton
      });

      const shouldConfirm = shouldConfirmNavigation();
      const infeasibleError = hasInfeasibleError();

      if ((shouldConfirm || infeasibleError) &&
        !navigationState.current.isNavigatingAway) {
        e.preventDefault();
        e.stopPropagation();

        const targetHref = navLink.getAttribute('href');

        navigationState.current.isNavigatingAway = true;
        navigationState.current.pendingNavigation = targetHref;

        if (infeasibleError) {

          showExitConfirmation({
            title: t('navigation.exitConfirm.title'),
            message: t('navigation.exitConfirm.message'),
            primaryButtonText: t('navigation.exitConfirm.saveAndExit'),
            secondaryButtonText: t('navigation.exitConfirm.cancel'),
            tertiaryButtonText: t('navigation.exitConfirm.exitWithoutSaving'),
            onSaveAndExit: () => {
              saveCurrentDraftAndNavigate(targetHref);
            },
            onExitWithoutSaving: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate(targetHref);
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        } else {

          showConfirmation({
            title: t('navigation.confirm.title'),
            message: t('navigation.confirm.message'),
            confirmText: t('navigation.confirm.confirmText'),
            cancelText: t('navigation.confirm.cancelText'),
            onConfirm: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;
              navigate(targetHref);
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        }

        return false;
      }
    };

    const handleDropdownClick = (e) => {
      const dropdownItem = e.target.closest('.user-dropdown-item');
      if (!dropdownItem) return;

      logDebugInfo('dropdownClick', { isDropdownItem: !!dropdownItem });

      const shouldConfirm = shouldConfirmNavigation();
      const infeasibleError = hasInfeasibleError();

      if ((shouldConfirm || infeasibleError) &&
        !navigationState.current.isNavigatingAway) {
        e.preventDefault();
        e.stopPropagation();

        const targetHref = dropdownItem.getAttribute('href') || '/';

        navigationState.current.isNavigatingAway = true;
        navigationState.current.pendingNavigation = targetHref;

        if (infeasibleError) {

          showExitConfirmation({
            title: t('navigation.exitConfirm.title'),
            message: t('navigation.exitConfirm.message'),
            primaryButtonText: t('navigation.exitConfirm.saveAndExit'),
            secondaryButtonText: t('navigation.exitConfirm.cancel'),
            tertiaryButtonText: t('navigation.exitConfirm.exitWithoutSaving'),
            onSaveAndExit: () => {
              saveCurrentDraftAndNavigate(targetHref);
            },
            onExitWithoutSaving: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;

              if (dropdownItem.classList.contains('logout-item')) {
                const logoutHandler = dropdownItem.onclick;
                if (typeof logoutHandler === 'function') {
                  logoutHandler.call(dropdownItem, e);
                }
              } else {
                navigate(targetHref);
              }
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        } else {

          showConfirmation({
            title: t('navigation.confirm.title'),
            message: t('navigation.confirm.message'),
            confirmText: t('navigation.confirm.confirmText'),
            cancelText: t('navigation.confirm.cancelText'),
            onConfirm: () => {
              sessionStorage.clear();
              navigationState.current.hasUnsavedChanges = false;
              navigationState.current.isNavigatingAway = false;

              if (dropdownItem.classList.contains('logout-item')) {
                const logoutHandler = dropdownItem.onclick;
                if (typeof logoutHandler === 'function') {
                  logoutHandler.call(dropdownItem, e);
                }
              } else {
                navigate(targetHref);
              }
            },
            onCancel: () => {
              navigationState.current.isNavigatingAway = false;
              navigationState.current.pendingNavigation = null;
            }
          });
        }

        return false;
      }
    };

    const handleBeforeUnload = (e) => {
      logDebugInfo('beforeunload');

      if (shouldConfirmNavigation()) {
        const message = 'Tem certeza que deseja sair? Todo o seu progresso será perdido.';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    const addHelperClasses = () => {
      document.querySelectorAll('header .dropdown a, header .dropdown button').forEach(el => {
        el.classList.add('user-dropdown-item');
      });

      document.querySelectorAll('header .dropdown button:has(.logout-icon), header .dropdown a:has(.logout-icon)').forEach(el => {
        el.classList.add('logout-item');
      });
    };

    addHelperClasses();

    const observer = new MutationObserver(addHelperClasses);
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleLogoClick, { capture: true });
    document.addEventListener('click', handleHeaderClick, { capture: true });
    document.addEventListener('click', handleLinkClick, { capture: true });
    document.addEventListener('click', handleDropdownClick, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLogoClick, { capture: true });
      document.removeEventListener('click', handleHeaderClick, { capture: true });
      document.removeEventListener('click', handleLinkClick, { capture: true });
      document.removeEventListener('click', handleDropdownClick, { capture: true });

      observer.disconnect();
    };
  }, [location, navigate, showConfirmation, showExitConfirmation]);

  const markChangesSaved = () => {
    navigationState.current.hasUnsavedChanges = false;
  };

  const forceNavigate = (path) => {
    navigationState.current.hasUnsavedChanges = false;
    navigate(path);
  };

  return {
    navigate: forceNavigate,
    markChangesSaved
  };
};

export default useFormNavigation;